import { ROOM_CODE_LENGTH, WS_PATH, type ClientMsg, type ServerMsg } from './protocol'

export function inviteUrl(code: string): string {
  const url = new URL(window.location.href)
  url.hash = ''
  url.search = `?room=${code}`
  return url.toString()
}

export function roomFromUrl(): string | null {
  const code = new URLSearchParams(window.location.search).get('room')
  return code ? code.toUpperCase().slice(0, ROOM_CODE_LENGTH) : null
}

/** ลบ ?room= ออกจากแถบที่อยู่หลังเข้าห้องแล้ว จะได้ไม่เด้งกลับห้องเดิมตอนรีเฟรช */
export function clearRoomFromUrl(): void {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('room')) return
  url.searchParams.delete('room')
  window.history.replaceState({}, '', url.pathname + url.search)
}

export function wsUrl(): string {
  const env = import.meta.env.VITE_MILLO_WS as string | undefined
  if (env) return env
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  // ตอน dev หน้าเว็บอยู่พอร์ต 5273 แต่เซิร์ฟเวอร์ห้องอยู่ 8788
  const host = import.meta.env.DEV ? `${window.location.hostname}:8788` : window.location.host
  return `${proto}//${host}${WS_PATH}`
}

export type NetStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed'

interface Handlers {
  onMessage: (msg: ServerMsg) => void
  onStatus: (status: NetStatus) => void
}

/**
 * WebSocket ที่ต่อกลับเองเมื่อหลุด และส่งข้อความที่ค้างอยู่ในคิวเมื่อต่อติด
 * `hello` คือข้อความแรกที่ต้องส่งทุกครั้งที่ต่อใหม่ (create / join / rejoin)
 */
export class NetClient {
  private ws: WebSocket | null = null
  private queue: ClientMsg[] = []
  private hello: ClientMsg | null = null
  private retries = 0
  private closedByUs = false
  private keepAlive: ReturnType<typeof setInterval> | null = null
  private retryTimer: ReturnType<typeof setTimeout> | null = null

  constructor(private h: Handlers) {}

  connect(hello: ClientMsg): void {
    this.hello = hello
    this.closedByUs = false
    this.open()
  }

  /** เปลี่ยนข้อความทักทายเป็น rejoin หลังได้ token แล้ว */
  setHello(hello: ClientMsg): void {
    this.hello = hello
  }

  send(msg: ClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg))
    else this.queue.push(msg)
  }

  close(): void {
    this.closedByUs = true
    if (this.retryTimer) clearTimeout(this.retryTimer)
    if (this.keepAlive) clearInterval(this.keepAlive)
    this.retryTimer = null
    this.keepAlive = null
    this.queue = []
    this.hello = null
    this.ws?.close()
    this.ws = null
    this.h.onStatus('idle')
  }

  private open(): void {
    this.h.onStatus(this.retries === 0 ? 'connecting' : 'reconnecting')
    let ws: WebSocket
    try {
      ws = new WebSocket(wsUrl())
    } catch {
      this.scheduleRetry()
      return
    }
    this.ws = ws

    ws.onopen = () => {
      this.retries = 0
      this.h.onStatus('open')
      if (this.hello) ws.send(JSON.stringify(this.hello))
      for (const m of this.queue.splice(0)) ws.send(JSON.stringify(m))
      if (this.keepAlive) clearInterval(this.keepAlive)
      this.keepAlive = setInterval(() => this.send({ t: 'ping' }), 25_000)
    }

    ws.onmessage = (ev) => {
      let msg: ServerMsg
      try {
        msg = JSON.parse(String(ev.data)) as ServerMsg
      } catch {
        return
      }
      if (msg.t === 'pong') return
      this.h.onMessage(msg)
    }

    ws.onclose = () => {
      if (this.keepAlive) clearInterval(this.keepAlive)
      this.keepAlive = null
      if (this.closedByUs) return
      this.scheduleRetry()
    }

    ws.onerror = () => ws.close()
  }

  private scheduleRetry(): void {
    if (this.closedByUs || !this.hello) {
      this.h.onStatus('closed')
      return
    }
    this.retries += 1
    if (this.retries > 12) {
      this.h.onStatus('closed')
      return
    }
    this.h.onStatus('reconnecting')
    const wait = Math.min(8000, 400 * 2 ** Math.min(this.retries, 5))
    this.retryTimer = setTimeout(() => this.open(), wait)
  }
}
